/**
 * Enquiries Page API
 * Page-level functions for server-side pagination, filtering, and sorting
 * API calls are handled by web/src/APIs/Enquiries/
 */

import { GetAllBookingsStatusEnum } from "collins-js-sdk";
import {
  Enquiry,
  EnquiriesResponse,
  EnquiryStatus,
  FetchEnquiriesParams,
  FilterOptions
} from "../../types/enquiries";
import { VenuePage } from "../../types/venues";
import {
  fetchPaginatedEnquiriesFromAPI,
  fetchFacetCountsFromServer,
  fetchFilteredTotalHeadsFromServer
} from "../../APIs/Enquiries/GetEnquiries";

// Re-export API functions for backwards compatibility
export {
  takeEnquiryRequest,
  updateEnquiryStatusRequest,
  deleteEnquiryRequest,
  rejectEnquiryRequest,
  confirmEnquiryRequest,
  assignEnquiryRequest,
  acknowledgeBookingNotifications,
  bulkAssignTo,
  bulkSetStatus,
  bulkAddLabels,
  bulkRemoveLabels,
  bulkSetFollowUp,
  bulkReject,
  bulkDelete
} from "../../APIs/Enquiries/EnquiryOperations";
export type { BulkDeleteReason } from "../../APIs/Enquiries/EnquiryOperations";

const DEFAULT_STATUSES: EnquiryStatus[] = ["new", "in_progress"];

/**
 * Maps an EnquiryStatus to the corresponding GetAllBookingsStatusEnum value(s).
 * Note: 'cancelled' in the client maps to 'rejected' in the API.
 */
const mapStatusToApiStatus = (
  status: EnquiryStatus
): GetAllBookingsStatusEnum[] => {
  switch (status) {
    case "new":
      return [GetAllBookingsStatusEnum.New];
    case "in_progress":
      return [GetAllBookingsStatusEnum.InProgress];
    case "complete":
      return [GetAllBookingsStatusEnum.Complete];
    case "cancelled":
      return [GetAllBookingsStatusEnum.Rejected];
    case "lost":
      return [GetAllBookingsStatusEnum.Lost];
    case "deleted":
      return [GetAllBookingsStatusEnum.Deleted];
    default:
      return [];
  }
};

/**
 * Maps an array of EnquiryStatus values to GetAllBookingsStatusEnum values.
 * When no statuses are provided, defaults to "new" and "in_progress" to match
 * the previous client-side filtering behaviour and collins-ui defaults
 * (bookings.js:187).
 */
const mapStatusesToApiStatuses = (
  statuses?: EnquiryStatus[]
): GetAllBookingsStatusEnum[] => {
  const effectiveStatuses = statuses?.length ? statuses : DEFAULT_STATUSES;
  return effectiveStatuses.flatMap(mapStatusToApiStatus);
};

/**
 * Maps an EnquiriesSortOption to the Collins API sort format.
 * The Collins API uses the convention of prefixing with '-' for descending
 * order, similar to collins-ui's BookingsProvider (bookingsProvider.js:28-39).
 */
const mapSortToApiFormat = (sort?: string): string[] | undefined => {
  switch (sort) {
    case "date_desc":
      return ["-date"];
    case "date_asc":
      return ["date"];
    case "customer_desc":
      return ["-first_name"];
    case "customer_asc":
      return ["first_name"];
    case "party_size_desc":
      return ["-num_people"];
    case "party_size_asc":
      return ["num_people"];
    case "booking_value_desc":
      return ["-booking_value"];
    case "booking_value_asc":
      return ["booking_value"];
    default:
      return undefined;
  }
};

/**
 * Filters enquiries by concierge status client-side.
 * The concierge filter is not supported by the Collins API, so it must
 * be applied after fetching the paginated results from the server.
 *
 * NOTE: When the concierge filter is active, the total count from the
 * server will not reflect the concierge filter, as it is applied
 * client-side on the current page only.
 */
const filterByConcierge = (
  enquiries: Enquiry[],
  concierge?: string
): Enquiry[] => {
  if (!concierge) return enquiries;
  if (concierge === "true")
    return enquiries.filter((e) => Boolean(e.concierge));
  if (concierge === "false") return enquiries.filter((e) => !e.concierge);
  return enquiries;
};

/**
 * Fetches enquiries with server-side filtering, sorting, and pagination.
 * Uses fetchPaginatedEnquiriesFromAPI for the paginated API call and
 * fetchFacetCountsFromServer for sidebar counts.
 *
 * This replaces the previous approach of fetching ALL enquiries and handling
 * filtering, sorting, and pagination client-side. The next batch of enquiries
 * is only fetched when the user interacts with the pagination controls.
 *
 * Pagination: Page number is calculated from the offset and limit parameters.
 * The Collins API uses 1-based page numbers with pagination headers
 * (X-Pagination-Total-Results, X-Pagination-Page, X-Pagination-Per-Page),
 * similar to collins-ui's getBookingsFromCollinsApi (bookingsManager.js:390-407).
 *
 * Server-side filters: status, assigned_to, query, venue, booking_type,
 * labels, notifications, partner_source, date range, sort.
 *
 * Client-side filters: concierge (not supported by the Collins API).
 *
 * The assigned_to filter is passed directly to the Collins API. When the
 * value is 'me', the Collins API includes both bookings assigned to the
 * current user AND unassigned bookings (BookingFilters.php:234-238),
 * matching the previous client-side behaviour of including NEW and
 * unassigned IN_PROGRESS enquiries.
 *
 * IMPORTANT: Facet counts are fetched from the server using facets_only=true,
 * similar to collins-ui's getBookingsCounts (bookingsManager.js:502-526).
 * The server call passes all filter criteria EXCEPT status, ensuring that
 * status counts remain accurate regardless of the currently selected status.
 */
export const fetchEnquiries = async (
  params: FetchEnquiriesParams & { venueIds: string[]; venues: VenuePage[] }
): Promise<EnquiriesResponse> => {
  const { venueIds, venues, filters, sort, offset = 0, limit = 15 } = params;

  // Calculate 1-based page number from offset and limit
  const page = Math.floor(offset / limit) + 1;

  // Map statuses to API format, defaulting to new/in_progress
  const apiStatuses = mapStatusesToApiStatuses(filters?.status);

  // Map sort option to Collins API format
  const apiSort = mapSortToApiFormat(sort);

  // Build filters without status for the facet counts request.
  // Status is excluded so that status counts remain accurate regardless of which
  // status filter is selected. assigned_to is preserved so that facet counts
  // reflect the user's assignment filter, matching collins-ui behaviour
  // where assigned_to='me' is sent in the facet counts request
  // (bookings.js:217-219, bookingsManager.js:502-526).
  const filtersForFacetCounts: FilterOptions | undefined = filters
    ? { ...filters, status: undefined }
    : undefined;

  // Fetch paginated enquiries, sidebar facet counts (without status), and
  // filtered totalHeads (with status) in parallel
  const [paginatedResponse, facetCounts, filteredTotalHeads] =
    await Promise.all([
      fetchPaginatedEnquiriesFromAPI({
        venueIds,
        venueFilter: filters?.venue,
        venues,
        statuses: apiStatuses,
        page,
        limit,
        sort: apiSort,
        assignedTo: filters?.assigned_to,
        query: filters?.query,
        bookingTypes: filters?.booking_type,
        labels: filters?.labels,
        notifications: filters?.notifications,
        partnerSource: filters?.partner_source,
        dateFrom: filters?.date_from,
        dateTo: filters?.date_to
      }),
      fetchFacetCountsFromServer(filtersForFacetCounts),
      fetchFilteredTotalHeadsFromServer(filters)
    ]);

  // Apply client-side concierge filter (not supported by the Collins API)
  const enquiries = filterByConcierge(
    paginatedResponse.enquiries,
    filters?.concierge
  );

  return {
    enquiries,
    total: paginatedResponse.total,
    offset,
    limit,
    facetCounts,
    filteredTotalHeads
  };
};
